/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-04
 */

// Add third-party dependencies.
import { Box, Typography, Chip, Link } from '@mui/material';
import {
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  BugReport as BugReportIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

// Add local dependencies.
import type { ChangelogChange as ChangelogChangeType } from '../../../types/changelog';
import { CHANGE_TYPE_CONFIG } from '../../../utils/changelogUtils';

/**
 * Props for the ChangelogChange component.
 */
interface ChangelogChangeProps {
  /** The change data to display */
  change: ChangelogChangeType;
  /** Whether to show the type chip */
  showType?: boolean;
}

/**
 * Map of icon names to actual icon components.
 */
const ICON_MAP = {
  StarIcon,
  TrendingUpIcon,
  BugReportIcon,
  WarningIcon,
};

/**
 * Component for displaying an individual changelog change item.
 * Shows the change message with optional type indicator and links.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The changelog change component
 *
 * @example
 * return (
 *   <ChangelogChange
 *     change={{
 *       type: 'feature',
 *       message: 'Added new dashboard component',
 *       pullRequest: 123
 *     }}
 *   />
 * );
 */
const ChangelogChange: React.FC<ChangelogChangeProps> = ({
  change,
  showType = false,
}) => {
  const config = CHANGE_TYPE_CONFIG[change.type];
  const IconComponent = config
    ? ICON_MAP[config.icon as keyof typeof ICON_MAP]
    : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        py: 0.5,
      }}
    >
      {/* Type indicator */}
      {showType && IconComponent && (
        <Box sx={{ mt: 0.5 }}>
          <IconComponent
            fontSize="small"
            color={config.color}
            sx={{ fontSize: 16 }}
          />
        </Box>
      )}

      {/* Change content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            mb: 0.5,
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          {change.message}
        </Typography>

        {/* Links to commit/PR */}
        {(change.commit || change.pullRequest) && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {change.commit && (
              <Link
                href={`https://github.com/shonlittle/tools-dance-frontend/commit/${change.commit}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '0.75rem', textDecoration: 'none' }}
              >
                {change.commit.substring(0, 7)}
              </Link>
            )}
            {change.pullRequest && (
              <Link
                href={`https://github.com/shonlittle/tools-dance-frontend/pull/${change.pullRequest}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '0.75rem', textDecoration: 'none' }}
              >
                #{change.pullRequest}
              </Link>
            )}
          </Box>
        )}
      </Box>

      {/* Type chip (alternative to icon) */}
      {showType && !IconComponent && config && (
        <Chip
          label={config.label}
          size="small"
          color={config.color}
          variant="outlined"
          sx={{ fontSize: '0.75rem', height: 24 }}
        />
      )}
    </Box>
  );
};

export default ChangelogChange;
